/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Injector, Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, Observable } from 'rxjs';

import { ObservableCollection, TextService, ChangedEvent, AlertService } from '@typescript-kit/core';

@Injectable()
export class ViewService<TViewModel extends ObservableCollection<any, any>, TComponentModel> {
  private readonly viewModelSubject: BehaviorSubject<TViewModel>;
  private readonly componentModelSubject: BehaviorSubject<TComponentModel>;
  private viewModelPropertyChangedSubscription: Subscription;

  protected readonly textService: TextService;
  protected readonly alertService: AlertService;

  constructor(public readonly injector: Injector) {
    this.viewModelSubject = new BehaviorSubject<TViewModel>(null);
    this.viewModelSubject.subscribe(() => this.onViewModelChanged());
    this.componentModelSubject = new BehaviorSubject<TComponentModel>(null);
    this.textService = injector.get(TextService);
    this.alertService = injector.get(AlertService);
  }

  get viewModel(): TViewModel {
    return this.viewModelSubject.getValue();
  }

  set viewModel(value: TViewModel) {
    if (this.viewModel === value) {
      return;
    }
    if (this.viewModelPropertyChangedSubscription) {
      this.viewModelPropertyChangedSubscription.unsubscribe();
    }
    this.viewModelPropertyChangedSubscription = value
      ? value.changedEvent.subscribe((event) => this.onViewModelPropertyChanged(event))
      : null;
    this.viewModelSubject.next(value);
  }

  get viewModelChanged(): Observable<TViewModel> {
    return this.viewModelSubject.asObservable();
  }

  get componentModel(): TComponentModel {
    return this.componentModelSubject.getValue();
  }

  set componentModel(value: TComponentModel) {
    if (this.componentModel === value) {
      return;
    }
    this.componentModelSubject.next(value);
  }

  get componentModelChanged(): Observable<TComponentModel> {
    return this.componentModelSubject.asObservable();
  }

  get isInitialized(): boolean {
    return !!this.viewModel;
  }

  get isFinalized(): boolean {
    return !this.viewModel;
  }

  initialize(viewModel: TViewModel): void {
    this.viewModel = viewModel;
    this.componentModel = this.createComponentModel();
  }

  finalize() {
    this.viewModel = null;
    this.componentModel = null;
  }

  protected onViewModelChanged() {
  }

  protected onViewModelPropertyChanged(event: ChangedEvent) {
    this.refreshComponentModel(this.componentModel);
  }

  protected createComponentModel(): TComponentModel {
    return null;
  }

  protected refreshComponentModel(componentModel: TComponentModel) {
    // DO NOTHING
  }

}
